import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Alert, Dropdown, Modal, Input, Select, Typography } from '@/shared/ui/primitives';
import { DownOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import RoomWantedTable from '../components/RoomWantedTable';
import { useDeleteRoomWanted } from '../hooks/useRoomWantedMutations';
import { useBulkWantedStatus, useBulkWantedDelete, useBulkWantedFlags } from '@/features/ops/hooks/useOps';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type FilterMetadataResponse } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { userApi } from '@/features/users/api/user.api';
import { nationalityApi } from '@/features/nationalities/api/nationality.api';
import { propertyAmenityApi } from '@/features/property-amenities/api/propertyAmenity.api';
import apiClient from '@/lib/apiClient';

const RESOURCE = ADMIN_FILTER_RESOURCES.roomWantedAds;

const ROOM_WANTED_FALLBACK_FILTER_METADATA: FilterMetadataResponse = {
  fields: [
    { key: 'id', label: 'ID', type: 'NUMBER', operators: ['EQUALS', 'GT', 'GTE', 'LT', 'LTE', 'BETWEEN'], sortable: true },
    { key: 'title', label: 'Title', type: 'TEXT', operators: ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH'], sortable: true },
    { key: 'status', label: 'Status', type: 'ENUM', operators: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN'], sortable: true, options: ['DRAFT', 'PUBLISHED', 'ARCHIVED', 'REJECTED'].map((value) => ({ value, label: value })) },
    { key: 'budget', label: 'Budget', type: 'NUMBER', operators: ['EQUALS', 'GT', 'GTE', 'LT', 'LTE', 'BETWEEN'], sortable: true },
    { key: 'smokingPreference', label: 'Smoking preference', type: 'ENUM', operators: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN'], sortable: true, options: ['SMOKER', 'NON_SMOKER', 'NO_PREFERENCE'].map((value) => ({ value, label: value })) },
    { key: 'rentType', label: 'Rent type', type: 'ENUM', operators: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN'], sortable: true, options: ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].map((value) => ({ value, label: value })) },
    { key: 'roomType', label: 'Room type', type: 'ENUM', operators: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN'], sortable: true, options: ['PRIVATE_ROOM', 'SHARED_ROOM', 'MASTER_ROOM', 'BED_SPACE'].map((value) => ({ value, label: value })) },
    { key: 'isFeatured', label: 'Featured', type: 'BOOLEAN', operators: ['EQUALS'], sortable: true },
    { key: 'createdAt', label: 'Created at', type: 'DATETIME', operators: ['BEFORE', 'AFTER', 'BETWEEN', 'LAST_DAYS'], sortable: true },
    { key: 'updatedAt', label: 'Updated at', type: 'DATETIME', operators: ['BEFORE', 'AFTER', 'BETWEEN', 'LAST_DAYS'], sortable: true },
    { key: 'user.id', label: 'User', type: 'NUMBER', operators: ['EQUALS'], sortable: false },
    { key: 'user.firstName', label: 'User first name', type: 'TEXT', operators: ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH'], sortable: false },
    { key: 'user.lastName', label: 'User last name', type: 'TEXT', operators: ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH'], sortable: false },
    { key: 'user.email', label: 'User email', type: 'TEXT', operators: ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH'], sortable: false },
    { key: 'user.phoneNumber', label: 'User phone', type: 'TEXT', operators: ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH'], sortable: false },
    { key: 'user.gender', label: 'User gender', type: 'ENUM', operators: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN'], sortable: false, options: ['MALE', 'FEMALE'].map((value) => ({ value, label: value })) },
    { key: 'user.occupationType', label: 'User occupation', type: 'ENUM', operators: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN'], sortable: false },
    { key: 'user.nationality.id', label: 'User nationality', type: 'NUMBER', operators: ['EQUALS'], sortable: false },
    { key: 'amenities.id', label: 'Amenity', type: 'NUMBER', operators: ['EQUALS'], sortable: false },
    { key: 'cityId', label: 'City', type: 'NUMBER', operators: ['EQUALS'], sortable: true },
    { key: 'selectedDistricts', label: 'District', type: 'NUMBER', operators: ['EQUALS'], sortable: false },
    { key: 'isSelectAllDistricts', label: 'All districts', type: 'BOOLEAN', operators: ['EQUALS'], sortable: true },
    { key: 'importSource', label: 'Import source', type: 'TEXT', operators: ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH'], sortable: true },
    { key: 'importSourceId', label: 'Import source ID', type: 'TEXT', operators: ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH'], sortable: true },
    { key: 'showPhoneNumber', label: 'Show phone', type: 'BOOLEAN', operators: ['EQUALS'], sortable: true },
    { key: 'showWhatsappContact', label: 'Show WhatsApp', type: 'BOOLEAN', operators: ['EQUALS'], sortable: true },
    { key: 'showOwnerInfo', label: 'Show owner info', type: 'BOOLEAN', operators: ['EQUALS'], sortable: true },
    { key: 'showInAppMessaging', label: 'Show in-app messaging', type: 'BOOLEAN', operators: ['EQUALS'], sortable: true },
    { key: 'hideExactLocation', label: 'Hide exact location', type: 'BOOLEAN', operators: ['EQUALS'], sortable: true },
  ],
  limits: { maxPageSize: 100, maxConditions: 20, maxDepth: 1 },
  defaultSort: [{ field: 'id', direction: 'DESC' }],
};

type WantedStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'REJECTED';

const FLAG_FIELDS = [
  { key: 'showPhoneNumber', labelKey: 'roomWanted.showPhone' },
  { key: 'showWhatsappContact', labelKey: 'roomWanted.showWhatsapp' },
  { key: 'showOwnerInfo', labelKey: 'roomWanted.showOwnerInfo' },
  { key: 'showInAppMessaging', labelKey: 'roomWanted.showInAppMessaging' },
  { key: 'hideExactLocation', labelKey: 'roomWanted.hideLocation' },
  { key: 'isFeatured', labelKey: 'roomWanted.featured' },
];

export default function RoomWantedListPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.toLowerCase().startsWith('ar');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<WantedStatus | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkFlagsOpen, setBulkFlagsOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [flagValues, setFlagValues] = useState<Record<string, boolean | undefined>>({});

  const list = useDynamicAdminList<any>(RESOURCE, { onFiltersChanged: () => setSelectedIds([]) });
  const { data, isLoading } = list.searchQuery as any;
  const deleteMutation = useDeleteRoomWanted();
  const bulkStatusMutation = useBulkWantedStatus();
  const bulkDeleteMutation = useBulkWantedDelete();
  const bulkFlagsMutation = useBulkWantedFlags();

  const relationLookups = useMemo(() => ({
    'user.id': {
      fetchFn: ({ search, page, size }: any) => userApi.list({ page, size, firstName: search }),
      fetchById: (id: string | number) => userApi.getById(id),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || `#${u.id}` }),
      placeholder: t('common.user'),
    },
    'user.nationality.id': {
      fetchFn: ({ search, page, size }: any) => nationalityApi.list({ page, size, countryNameEn: search }),
      fetchById: (id: string | number) => nationalityApi.getById(id),
      mapOption: (n: any) => ({ value: n.id, label: n.countryNameEn || n.nameEn || n.countryNameAr || `#${n.id}` }),
      placeholder: t('filters.nationality'),
    },
    'amenities.id': {
      fetchFn: ({ search, page, size }: any) => propertyAmenityApi.list({ page, size, nameEn: search }),
      fetchById: (id: string | number) => propertyAmenityApi.getById(id),
      mapOption: (a: any) => ({ value: a.id, label: isArabic ? a.nameAr || a.nameEn || `#${a.id}` : a.nameEn || a.nameAr || `#${a.id}` }),
      placeholder: t('roomWanted.amenities'),
    },
    cityId: {
      fetchFn: ({ search, page, size }: any) => fetchPublicGeoList('/public/geo/cities', { search, page, size }),
      mapOption: (c: any) => ({ value: c.id, label: geoName(c, isArabic) }),
      placeholder: t('common.city'),
    },
    selectedDistricts: {
      fetchFn: ({ search, page, size }: any) => fetchPublicGeoList('/public/geo/districts', { search, page, size }),
      mapOption: (d: any) => ({ value: d.id, label: geoName(d, isArabic) }),
      placeholder: t('common.district'),
    },
  }), [isArabic, t]);
  const filterMetadata = list.metadataQuery.data?.fields?.length ? list.metadataQuery.data : ROOM_WANTED_FALLBACK_FILTER_METADATA;

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  const confirmBulkStatus = () => {
    if (!bulkStatus) return;
    bulkStatusMutation.mutate(
      { adIds: selectedIds, status: bulkStatus, rejectReason: rejectReason.trim() || undefined },
      { onSettled: () => { setBulkStatus(null); setRejectReason(''); setSelectedIds([]); } },
    );
  };

  const confirmBulkDelete = () => {
    bulkDeleteMutation.mutate(selectedIds, {
      onSettled: () => { setBulkDeleteOpen(false); setSelectedIds([]); },
    });
  };

  const confirmBulkFlags = () => {
    const flags: Record<string, boolean | undefined> = {};
    for (const [k, v] of Object.entries(flagValues)) {
      if (v !== undefined) flags[k] = v;
    }
    bulkFlagsMutation.mutate(
      { adIds: selectedIds, flags },
      { onSettled: () => { setBulkFlagsOpen(false); setFlagValues({}); setSelectedIds([]); } },
    );
  };

  const bulkMenu = {
    items: [
      { key: 'publish', label: t('adOps.bulk.publish'), onClick: () => setBulkStatus('PUBLISHED') },
      { key: 'reject', label: t('adOps.bulk.reject'), onClick: () => setBulkStatus('REJECTED') },
      { key: 'archive', label: t('adOps.bulk.archive'), onClick: () => setBulkStatus('ARCHIVED') },
      { key: 'flags', label: t('adOps.bulk.flags'), onClick: () => setBulkFlagsOpen(true) },
      { type: 'divider' as const },
      { key: 'delete', label: t('adOps.bulk.deletePermanently'), danger: true, onClick: () => setBulkDeleteOpen(true) },
    ],
  };

  const flagSelectOptions = [
    { value: '__unchanged__', label: t('adOps.bulk.flagUnchanged') },
    { value: 'true', label: t('common.yes') },
    { value: 'false', label: t('common.no') },
  ];

  return (
    <div>
      <PageHeader
        title={t('roomWanted.title')}
        subtitle={t('roomWanted.subtitle')}
      />
      <DynamicFilterBuilder resource={list.resource} metadata={filterMetadata}
        loading={list.metadataQuery.isLoading}
        value={list.filters}
        relationLookups={relationLookups}
        onChange={list.setFilters}
        onReset={list.resetFilters}
      />

      {selectedIds.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('adOps.bulk.selected', { count: selectedIds.length })}
          action={
            <Space>
              <Dropdown menu={bulkMenu} trigger={['click']}>
                <Button type="primary">{t('adOps.bulk.action')} <DownOutlined /></Button>
              </Dropdown>
              <Button onClick={() => setSelectedIds([])}>{t('userOps.bulk.clear')}</Button>
            </Space>
          }
        />
      )}

      <div style={{ marginTop: 16 }}>
        <RoomWantedTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onView={(id) => navigate(`/room-wanted/${id}`)}
          onEdit={(id) => navigate(`/room-wanted/${id}/edit`)}
          onDelete={(item) => setDeleteTarget(item)}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>

      <ConfirmDialog open={!!deleteTarget} title={t('roomWanted.deleteTitle')}
        message={`${t('roomWanted.deleteMessage', { id: deleteTarget?.id, title: deleteTarget?.title || '' })}`}
        onConfirm={handleConfirmDelete} onCancel={() => setDeleteTarget(null)} loading={deleteMutation.isPending} />

      <Modal
        open={!!bulkStatus}
        onCancel={() => { setBulkStatus(null); setRejectReason(''); }}
        onOk={confirmBulkStatus}
        okText={bulkStatus === 'REJECTED' ? t('adOps.bulk.rejectAll') : t('adOps.bulk.setStatus', { status: bulkStatus ?? '' })}
        okButtonProps={{ danger: bulkStatus === 'REJECTED', loading: bulkStatusMutation.isPending }}
        title={bulkStatus ? t('adOps.bulk.title', { status: bulkStatus, count: selectedIds.length }) : ''}
      >
        {bulkStatus === 'REJECTED' && (
          <Input.TextArea
            rows={3}
            placeholder={t('adOps.bulk.rejectReasonPlaceholder')}
            value={rejectReason}
            onChange={(e: any) => setRejectReason(e.target.value)}
            data-allstak-mask="true"
            data-sensitive="true"
          />
        )}
        {bulkStatus !== 'REJECTED' && <p>{t('adOps.bulk.statusHint', { count: selectedIds.length })}</p>}
      </Modal>

      <Modal
        open={bulkDeleteOpen}
        onCancel={() => setBulkDeleteOpen(false)}
        onOk={confirmBulkDelete}
        okText={t('adOps.bulk.deletePermanently')}
        okButtonProps={{ danger: true, loading: bulkDeleteMutation.isPending }}
        title={t('adOps.bulk.deleteTitle', { count: selectedIds.length })}
      >
        <p>{t('adOps.bulk.deleteWarn', { count: selectedIds.length })}</p>
      </Modal>

      <Modal
        open={bulkFlagsOpen}
        onCancel={() => { setBulkFlagsOpen(false); setFlagValues({}); }}
        onOk={confirmBulkFlags}
        okText={t('adOps.bulk.applyFlags')}
        okButtonProps={{ loading: bulkFlagsMutation.isPending }}
        title={t('adOps.bulk.flagsTitle', { count: selectedIds.length })}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {FLAG_FIELDS.map(({ key, labelKey }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Typography.Text style={{ minWidth: 160 }}>{t(labelKey)}</Typography.Text>
              <Select
                style={{ flex: 1 }}
                value={flagValues[key] === undefined ? '__unchanged__' : String(flagValues[key])}
                onChange={(v: string) => setFlagValues((prev) => ({
                  ...prev,
                  [key]: v === '__unchanged__' ? undefined : v === 'true',
                }))}
                options={flagSelectOptions}
              />
            </div>
          ))}
        </Space>
      </Modal>
    </div>
  );
}

function geoName(item: any, isArabic: boolean) {
  if (isArabic) return item.nameAr || item.name || item.nameEn || `#${item.id}`;
  return item.nameEn || item.name || item.nameAr || `#${item.id}`;
}

async function fetchPublicGeoList(endpoint: string, params: { search?: string; page: number; size: number }) {
  const res = await apiClient.get(endpoint, {
    params: {
      page: params.page,
      size: params.size,
      search: params.search || undefined,
    },
  });
  const data = res.data?.data ?? {};
  const content = Array.isArray(data.content) ? data.content : [];
  return {
    content,
    totalPages: data.totalPages ?? 1,
    totalItems: data.totalItems ?? data.totalElements ?? content.length,
    currentPage: data.currentPage ?? params.page,
  };
}
